import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuthContext } from './AuthContext';

export interface PlanData {
  plan: {
    slug: string;
    name: string;
    price_monthly: string;
  };
  features: Record<string, string>;
  usage: {
    templates: number;
    team_members: number;
  };
  limits: {
    templates: number | null;
    team_members: number | null;
    document_retention_days: number | null;
  };
  upgrade_plan: string | null;
}

interface PlanContextType {
  plan: PlanData | null;
  loading: boolean;
  refresh: () => Promise<void>;
  canUse: (feature: string) => boolean;
  isAtLimit: (feature: string) => boolean;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const [plan, setPlan] = useState<PlanData | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchPlan = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const response = await api.get('/plan');
      // api.get returns response.json() directly — the API returns { plan, features, usage, limits, upgrade_plan }
      const raw = response as any;
      const data: PlanData = raw.plan ? raw : raw.data?.plan ? raw.data : null;
      if (data) setPlan(data);
    } catch (err) {
      console.error('Failed to fetch plan data:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const canUse = useCallback((feature: string): boolean => {
    if (!plan) return true; // Allow by default if plan not loaded yet
    const value = plan.features[feature];
    return value !== undefined && value !== 'false' && value !== '0';
  }, [plan]);

  const isAtLimit = useCallback((feature: string): boolean => {
    if (!plan) return false;
    if (feature === 'templates') {
      return plan.limits.templates !== null && plan.usage.templates >= plan.limits.templates;
    }
    if (feature === 'team_members') {
      return plan.limits.team_members !== null && plan.usage.team_members >= plan.limits.team_members;
    }
    return false;
  }, [plan]);

  return (
    <PlanContext.Provider value={{ plan, loading, refresh: fetchPlan, canUse, isAtLimit }}>
      {children}
    </PlanContext.Provider>
  );
};
